import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { getDeductionList } from "../../services";
import { DeductionRouteRow } from "../../types/deduction.type";
import { formatNumber } from "../../utils";
import { formatDate } from "../../utils/date";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import DeductionFilter from "./Filter";
import DeductionPagination from "./Pagination";
import Skeleton from "@mui/material/Skeleton";

const COLUMN_COUNT = 5;

const renderSkeletonRows = () => {
  return Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

const headerCellSx = {
  borderRight: (theme: any) => `1px solid ${theme.palette.draft.main}`,
};

const LIMIT = 10;

export default function DeductionList() {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<DeductionRouteRow[]>([]);
  // Default ไปเดือนปัจจุบัน
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    dayjs().startOf("month"),
  );
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedRoute, setSelectedRoute] = useState<string>("all");

  const onGetDeductionList = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: {
          data: { count, rows },
        },
      } = await getDeductionList(
        page,
        limit,
        selectedDate,
        selectedBranch,
        selectedRoute,
      );

      setRows(rows);
      setTotalCount(count);
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedDate, selectedBranch, selectedRoute]);

  useEffect(() => {
    onGetDeductionList();
  }, [onGetDeductionList]);

  // รีเซ็ตไปหน้าแรกเมื่อเปลี่ยนฟิลเตอร์
  useEffect(() => {
    setPage(1);
  }, [selectedDate, selectedBranch, selectedRoute]);

  const openDetail = (row: DeductionRouteRow) => {
    const params = new URLSearchParams({ year_month: row.year_month });
    navigate(
      `/report/deduction/route/${row.route_uuid}?${params.toString()}`,
    );
  };

  return (
    <Paper>
      <DeductionFilter
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        selectedRoute={selectedRoute}
        setSelectedRoute={setSelectedRoute}
      />
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              <TableCell sx={headerCellSx}>เดือน-ปี</TableCell>
              <TableCell sx={headerCellSx}>สาขา</TableCell>
              <TableCell sx={headerCellSx}>สายส่ง</TableCell>
              <TableCell sx={headerCellSx}>จำนวนเงินที่หัก</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row, index) => (
                <TableRow
                  key={`${row.route_uuid}-${row.year_month}`}
                  sx={{
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td, & th": { padding: "0px 8px" },
                  }}
                >
                  <TableCell>
                    {formatDate(`${row.year_month}-01`, "MMMM-YYYY")}
                  </TableCell>
                  <TableCell>{row.branch_name}</TableCell>
                  <TableCell>
                    <IconButton
                      sx={{
                        bgcolor: (theme) => theme.palette.primary.main + "1A",
                        cursor: "default",
                        "&:hover, &:focus": {
                          bgcolor: (theme) => theme.palette.primary.main + "1A",
                        },
                        mr: 1,
                      }}
                    >
                      <AirportShuttleOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                    {row.route_code}
                  </TableCell>
                  <TableCell>
                    <Typography color="error">
                      -{formatNumber(row.total_amount)} บาท
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="info"
                      sx={{ p: 1.5, gap: 1 }}
                      onClick={() => openDetail(row)}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="subtitle2">ดูรายละเอียด</Typography>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DeductionPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
